import patientListService from '../services/patientListService';
import orderService from '../services/orderService';
import { transSericeApolloClient } from '@/graphql/client';
import { LIST_PATIENTS } from '@/graphql/queries/my-patients';

/**
 * Get patient information by patient ID
 * @param {string|number} patientId - Patient ID
 * @returns {Promise} API response with patient data
 */
export function getPatientByIdApi(patientId) {
  return orderService({
    url: '/orderTest/patient',
    method: 'get',
    params: {
      patientId: patientId
    }
  });
}

/**
 * Update patient contact and address information
 * @param {Object} data - Patient data to update
 * @param {number} data.patient_id - Patient ID (required)
 * @param {string} [data.birth_date] - Birth date (optional)
 * @param {string} [data.gender] - Gender (optional)
 * @param {string} [data.address_type] - Address type (optional)
 * @param {string} [data.street_address] - Street address (optional)
 * @param {string} [data.APT_PO] - Apartment/PO Box (optional)
 * @param {string} [data.city] - City (optional)
 * @param {string} [data.state] - State (optional)
 * @param {string} [data.zipcode] - Zip code (optional)
 * @param {string} [data.country] - Country (optional)
 * @param {boolean} [data.address_confirmed] - Address confirmed flag (optional)
 * @param {boolean} [data.is_primary_address] - Is primary address flag (optional)
 * @param {string} [data.phone] - Phone number (optional)
 * @param {string} [data.email] - Email (optional)
 * @param {string} [data.patient_language] - Patient language (optional)
 * @returns {Promise} API response
 */
export function updatePatientInfoApi(data) {
  // Build request body with only provided fields
  const requestBody = {};
  
  // Required field
  if (data.patient_id !== undefined) requestBody.patient_id = data.patient_id;
  
  // Optional fields - only include if provided
  // Support both new keys and legacy patient_* keys for backward compatibility
  if (data.birth_date || data.patient_birthdate) requestBody.birth_date = data.birth_date || data.patient_birthdate;
  if (data.gender || data.patient_gender) requestBody.gender = data.gender || data.patient_gender;
  if (data.address_type) requestBody.address_type = data.address_type;
  if (data.street_address) requestBody.street_address = data.street_address;
  if (data.APT_PO) requestBody.APT_PO = data.APT_PO;
  if (data.city) requestBody.city = data.city;
  if (data.state) requestBody.state = data.state;
  if (data.zipcode) requestBody.zipcode = data.zipcode;
  if (data.country) requestBody.country = data.country;
  if (data.address_confirmed !== undefined) requestBody.address_confirmed = data.address_confirmed;
  if (data.is_primary_address !== undefined) requestBody.is_primary_address = data.is_primary_address;
  if (data.phone || data.patient_phone) requestBody.phone = data.phone || data.patient_phone;
  if (data.email || data.patient_email) requestBody.email = data.email || data.patient_email;
  if (data.patient_language) requestBody.patient_language = data.patient_language;

  return patientListService({
    url: '/utility/updatePatientInfo',
    method: 'put',
    data: requestBody
  });
}

/**
 * Fetch patients by list of patient IDs using GraphQL
 * @param {string[]} patientIds - Array of patient IDs
 * @returns {Promise} Promise with patient data including names
 */
export async function fetchPatientsByIdsApi(patientIds) {
  try {
    if (!patientIds || patientIds.length === 0) {
      return [];
    }

    const response = await transSericeApolloClient.query({
      query: LIST_PATIENTS,
      variables: { 
        patientIds: patientIds.map(id => String(id))
      },
      fetchPolicy: 'network-only'
    });

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in fetchPatientsByIds:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response.data?.listpatients?.data?.response || [];
  } catch (error) {
    console.error('Error fetching patients by IDs:', error);
    throw error;
  }
}
